<script setup lang="ts">
import FooterFondOf from '@fondof-ui/components/footer/FooterFondOf.vue'
import FooterLegal from '@fondof-ui/components/footer/FooterLegal.vue'
import FooterOurBrands from '@fondof-ui/components/footer/FooterOurBrands.vue'
import FooterRoot from '@fondof-ui/components/footer/FooterRoot.vue'
import type { FooterSocialLinkItem, LocaleLinkItem } from '@fondof-ui/types'
import FooterAbout from '@satch-ui/components/footer/FooterAbout.vue'
import FooterAwards from '@satch-ui/components/footer/FooterAwards.vue'
import FooterCompany from '@satch-ui/components/footer/FooterCompany.vue'
import FooterContact from '@satch-ui/components/footer/FooterContact.vue'
import FooterDownload from '@satch-ui/components/footer/FooterDownload.vue'
import FooterLanguage from '@satch-ui/components/footer/FooterLanguage.vue'
import FooterPayment from '@satch-ui/components/footer/FooterPayment.vue'
import FooterService from '@satch-ui/components/footer/FooterService.vue'
import FooterShipping from '@satch-ui/components/footer/FooterShipping.vue'
import FooterSocials from '@satch-ui/components/footer/FooterSocials.vue'
import type { FooterData } from '@satch-ui/components/footer/types'
import config from '@storefront-satch/config'

import { GotyaSatchIcon } from '@fond-of/fondof-icons'

const props = defineProps<{
  data: FooterData
  socials: FooterSocialLinkItem[]
  locales: LocaleLinkItem[]
}>()

const [service, aboutSatch, downloads, company] = props.data.navigationLinks
</script>

<template>
  <footer>
    <FooterRoot class="xl:px-30 2xl:px-40">
      <div
        :class="[
          'grid w-full',
          'md:grid-cols-subgrid md:grid-rows-subgrid',
          'md:col-start-[first-col] md:col-end-[third-col] md:row-start-[first-row] md:row-end-[third-row]',
        ]"
      >
        <FooterService
          v-if="service?.label && service?.navigationItemsCollection?.items"
          :id="service.sys.id"
          :label="service.label"
          :items="service.navigationItemsCollection.items"
        />
        <FooterAbout
          v-if="
            aboutSatch?.label && aboutSatch?.navigationItemsCollection?.items
          "
          :id="aboutSatch.sys.id"
          :label="aboutSatch.label"
          :items="aboutSatch.navigationItemsCollection.items"
        />
        <FooterDownload
          v-if="downloads?.label && downloads?.navigationItemsCollection?.items"
          :id="downloads.sys.id"
          :label="downloads.label"
          :items="downloads.navigationItemsCollection.items"
        />
        <FooterCompany
          v-if="company?.label && company.navigationItemsCollection?.items"
          :id="company.sys.id"
          :label="company.label"
          :items="company.navigationItemsCollection.items"
        />
      </div>
      <FooterShipping
        v-if="
          props.data?.saleContent?.shipping?.text &&
          props.data?.saleContent?.shipping?.headline
        "
        :id="props.data?.saleContent?.shipping?.sys.id"
        :headline="props.data?.saleContent?.shipping?.headline"
        :content="props.data?.saleContent?.shipping?.text"
      />

      <FooterPayment
        v-if="
          props.data?.saleContent?.payments?.text &&
          props.data?.saleContent?.payments?.headline
        "
        :id="props.data?.saleContent?.payments?.sys.id"
        :headline="props.data?.saleContent?.payments?.headline"
        :content="props.data?.saleContent?.payments?.text"
      />
      <FooterAwards
        v-if="props.data?.awards?.text && props.data?.awards?.headline"
        :id="props.data?.awards?.sys.id"
        :headline="props.data?.awards?.headline"
        :content="props.data?.awards?.text"
      />
      <FooterSocials :socials="props.socials" />
      <FooterLanguage :locales="props.locales" />
    </FooterRoot>
    <FooterRoot class="footer-legal bg-gray-ultra-light xl:px-30 2xl:px-40">
      <FooterFondOf class="text-black" />
      <FooterContact
        v-if="props.data?.contact?.text"
        :id="props.data.contact.sys.id"
        :content="props.data.contact.text"
      />
      <FooterOurBrands
        class="text-black"
        :utm-params-footer="config.crossbrand.utmParamsFooter"
      >
        <template #default="{ currentLocale }">
          <li class="h-7.5 flex w-auto items-center justify-center pb-1">
            <a
              :href="`https://www.satch.com/${currentLocale}${config.crossbrand.utmParamsFooter || ''}`"
              target="_blank"
              class="flex h-full items-center"
            >
              <GotyaSatchIcon class="h-full w-auto" />
            </a>
          </li>
        </template>
      </FooterOurBrands>
      <FooterLegal :items="props.data.legalNavigationItems" />
    </FooterRoot>
  </footer>
</template>
<style lang="css" scoped>
:deep(.footer-legal span) {
  /* TODO: remove when legacy stack is no longer used  */
  color: theme('colors.black') !important;
}

:deep(.footer-legal a) {
  /* TODO: remove when legacy stack is no longer used  */
  color: theme('colors.black') !important;
}

:deep(.headline-style) {
  font-family: theme('fontFamily.sans-semi-bold') !important;
  font-weight: 600;
  /* TODO: remove when legacy stack is no longer used  */
  color: theme('colors.black') !important;
}
</style>
